import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import { openFilePreview } from '../common/FilePreviewModal';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown';
import PopoutWindow from '../common/PopoutWindow';
import JobForm from '../documentation/JobForm';
import '../admin/EmailTemplateModal.css';

const API_BASE = '/api/admin/quick-references/';
const JOB_SEARCH_URL = '/api/documentation/jobs/search_by_idf/';
const JOBS_URL = '/api/documentation/jobs/';

const JOB_SEARCH_FIELDS = [
    { id: 'idf_no', text: 'IDF NO.' },
    { id: 'bl_no', text: 'BL NO.' },
    { id: 'job_no', text: 'JOB NO.' },
];

const FONT_OPTIONS = ['Segoe UI', 'Arial', 'Aptos', 'Times New Roman', 'Calibri', 'Verdana'];
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18];

// Extract the font a Details body uses so the ribbon can reflect it
const detectFontFromHtml = (html) => {
    if (!html) return null;
    try {
        const container = document.createElement('div');
        container.innerHTML = html;
        let font = '';
        const styledEl = [...container.querySelectorAll('[style]')].find((el) => el.style.fontFamily);
        if (styledEl) font = styledEl.style.fontFamily;
        if (!font) {
            const fontEl = container.querySelector('font[face]');
            if (fontEl) font = fontEl.getAttribute('face') || '';
        }
        font = font.split(',')[0].trim().replace(/^["']+|["']+$/g, '');
        return font || null;
    } catch {
        return null;
    }
};

const getJobDisplay = (opt) => [
    opt.idf_no,
    opt.customer_short_name || opt.customer,
    opt.tansad_no,
    opt.bl_no,
].filter(Boolean).join(' | ');

const QuickReferenceForm = ({
    mode = 'add', // 'add' | 'edit' | 'view'
    initialData = null, // QuickReference row
    departments = [],
    canDelete = false,
    onClose,
    onSave,
    toastContainerId,
}) => {
    const row = initialData || {};
    const readOnly = mode === 'view';
    const editable = mode === 'add' || mode === 'edit';

    const notify = useCallback((type, msg) => {
        toast[type](msg, toastContainerId ? { containerId: toastContainerId } : undefined);
    }, [toastContainerId]);

    const [subject, setSubject] = useState(row.subject || '');
    const [files, setFiles] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState(row.attachments || []);
    const [removeIds, setRemoveIds] = useState([]);
    const [deptIds, setDeptIds] = useState((row.allowed_department_ids || []).map(String));
    const [saving, setSaving] = useState(false);
    const [deciding, setDeciding] = useState('');
    const [rejectPrompt, setRejectPrompt] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    // Job link — field selector + type-ahead like the application header search
    const [jobField, setJobField] = useState('idf_no');
    const [jobTerm, setJobTerm] = useState('');
    const [jobOptions, setJobOptions] = useState([]);
    const [jobDropdownOpen, setJobDropdownOpen] = useState(false);
    const [jobActiveIdx, setJobActiveIdx] = useState(-1);
    const [jobSearching, setJobSearching] = useState(false);
    const [resolvedJob, setResolvedJob] = useState(
        row.job ? { id: row.job, label: row.job_label } : null
    );
    const [jobPreview, setJobPreview] = useState({ open: false, job: null });
    const [jobLoading, setJobLoading] = useState(false);

    const openJobPreview = async () => {
        if (!resolvedJob) return;
        setJobLoading(true);
        try {
            const res = await fetch(`${JOBS_URL}${resolvedJob.id}/`, { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error('Failed to load job');
            const job = await res.json();
            setJobPreview({ open: true, job });
        } catch (e) {
            notify('error', e.message || 'Unable to open job');
        } finally {
            setJobLoading(false);
        }
    };

    const detailsRef = useRef(null);
    const savedRange = useRef(null);
    const [fontFamily, setFontFamily] = useState(detectFontFromHtml(row.details) || 'Segoe UI');
    const [fontSize, setFontSize] = useState('');

    // Initialize the Details editor content once — rewriting innerHTML on
    // every keypress resets the caret.
    useEffect(() => {
        if (detailsRef.current) {
            detailsRef.current.innerHTML = row.details || '';
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchJobOptions = useCallback(async (term, field) => {
        if (!term || term.length < 2) { setJobOptions([]); setJobDropdownOpen(false); return; }
        setJobSearching(true);
        setJobDropdownOpen(true);
        try {
            const res = await fetch(
                `${JOB_SEARCH_URL}?search_field=${encodeURIComponent(field)}&search=${encodeURIComponent(term)}`,
                { headers: getAuthHeaders() }
            );
            if (res.ok) {
                const data = await res.json();
                setJobOptions(Array.isArray(data) ? data : []);
            } else {
                console.error('Job search failed:', res.status, await res.text().catch(() => ''));
                setJobOptions([]);
            }
        } catch (err) {
            console.error('Job search error:', err);
            setJobOptions([]);
        } finally { setJobSearching(false); }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => fetchJobOptions(jobTerm, jobField), 400);
        return () => clearTimeout(t);
    }, [jobTerm, jobField, fetchJobOptions]);

    const selectJobOption = (opt) => {
        setResolvedJob({
            id: opt.id,
            label: opt.idf_no || opt.bl_no || opt.mbl_no || opt.hbl_so_no || `JOB-${opt.id}`,
        });
        setJobTerm('');
        setJobOptions([]);
        setJobDropdownOpen(false);
        setJobActiveIdx(-1);
    };

    const handleDetailsInput = () => {
        // Content is read from detailsRef on save; nothing else to sync.
    };

    // The editor lives inside a PopoutWindow, so execCommand and selection
    // calls must run on the document/window that owns the editable element,
    // not the main window's.
    const editorDoc = () => (detailsRef.current && detailsRef.current.ownerDocument) || document;
    const editorWin = () => editorDoc().defaultView || window;

    // Selecting a toolbar control moves focus out of the editor and
    // collapses the selection — capture it first, then restore before
    // running execCommand.
    const saveSelection = () => {
        const sel = editorWin().getSelection();
        if (sel.rangeCount > 0 && detailsRef.current && detailsRef.current.contains(sel.anchorNode)) {
            savedRange.current = sel.getRangeAt(0).cloneRange();
        }
    };

    const restoreSelection = () => {
        if (!savedRange.current) return;
        const sel = editorWin().getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRange.current);
    };

    const formatText = (command, value = null) => {
        editorDoc().execCommand(command, false, value);
        detailsRef.current.focus();
    };

    const applyFontName = (font) => {
        setFontFamily(font);
        detailsRef.current.focus();
        restoreSelection();
        formatText('fontName', font);
    };

    const applyFontSize = (px) => {
        if (!detailsRef.current || !px) return;
        setFontSize(px);
        detailsRef.current.focus();
        restoreSelection();
        // execCommand('fontSize') only supports the legacy 1-7 scale, so
        // insert size 7 as a marker then rewrite it to a real pixel size.
        editorDoc().execCommand('styleWithCSS', false, false);
        editorDoc().execCommand('fontSize', false, '7');
        detailsRef.current.querySelectorAll('font[size="7"]').forEach((el) => {
            el.removeAttribute('size');
            el.style.fontSize = `${px}px`;
        });
    };

    const applyColor = (command, color) => {
        detailsRef.current.focus();
        restoreSelection();
        const doc = editorDoc();
        if (command === 'hiliteColor') {
            if (!doc.execCommand('hiliteColor', false, color)) {
                doc.execCommand('backColor', false, color);
            }
        } else {
            doc.execCommand(command, false, color);
        }
    };

    const handleSave = async () => {
        if (!subject.trim()) { notify('error', 'Subject is required.'); return; }
        if (jobTerm.trim() && !resolvedJob) {
            notify('error', 'Select a job from the search results, or clear the job field.');
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('subject', subject.trim().toUpperCase());
            fd.append('details', detailsRef.current ? detailsRef.current.innerHTML : '');
            if (resolvedJob) {
                fd.append('job_id', resolvedJob.id);
            } else {
                fd.append('job_ref', '');
            }
            files.forEach(file => fd.append('attachments', file));
            removeIds.forEach(id => fd.append('remove_attachment_ids', id));
            if (canDelete) {
                deptIds.forEach(id => fd.append('allowed_department_ids', id));
            }
            const isEdit = mode === 'edit';
            const res = await fetch(isEdit ? `${API_BASE}${row.id}/` : API_BASE, {
                method: isEdit ? 'PATCH' : 'POST',
                headers: getAuthHeaders(true),
                body: fd,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.job_ref?.[0] || data.detail || 'Failed to save');
            notify('success', isEdit ? 'Quick Reference updated.' : 'Quick Reference created.');
            if (onSave) onSave();
            else if (onClose) onClose();
        } catch (e) {
            notify('error', e.message || 'Failed to save');
            setSaving(false);
        }
    };

    const handleDecision = async (decision, reason = '') => {
        if (!row.id) return;
        setDeciding(decision);
        try {
            const res = await fetch(`${API_BASE}${row.id}/${decision}/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ reason }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || data.detail || `Failed to ${decision}`);
            notify('success', decision === 'approve' ? 'Quick Reference approved.' : 'Quick Reference rejected.');
            if (onSave) onSave();
            else if (onClose) onClose();
        } catch (e) {
            notify('error', e.message || `Failed to ${decision}`);
            setDeciding('');
        }
    };

    const status = row.status || 'PENDING';

    return (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, background: '#fff', minHeight: '100%' }}>
            <h3 style={{ margin: 0, borderBottom: '1px solid #e0e0e0', paddingBottom: 8 }}>
                {mode === 'add' ? 'Add Quick Reference' : mode === 'edit' ? 'Edit Quick Reference' : 'View Quick Reference'}
                {status !== 'APPROVED' && (
                    <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 'bold', color: status === 'REJECTED' ? '#c62828' : '#b8860b' }}>
                        [{status}]
                    </span>
                )}
            </h3>

            <label style={{ fontSize: 13, fontWeight: 'bold' }}>
                Subject <span style={{ color: 'red' }}>*</span>
                <input
                    type="text"
                    className="search-input"
                    style={{ width: '100%', marginTop: 4, textTransform: 'uppercase' }}
                    value={subject}
                    onChange={e => setSubject(e.target.value.toUpperCase())}
                    readOnly={readOnly}
                />
            </label>

            <div>
                <label style={{ fontSize: 13, fontWeight: 'bold' }}>Details</label>
                <div className="email-template-toolbar" style={{ marginTop: 4 }}>
                    <button type="button" onClick={() => formatText('bold')} title="Bold" className="toolbar-btn" disabled={readOnly}>
                        <b>B</b>
                    </button>
                    <button type="button" onClick={() => formatText('italic')} title="Italic" className="toolbar-btn" disabled={readOnly}>
                        <i>I</i>
                    </button>
                    <button type="button" onClick={() => formatText('underline')} title="Underline" className="toolbar-btn" disabled={readOnly}>
                        <u>U</u>
                    </button>
                    <button type="button" onClick={() => formatText('insertUnorderedList')} title="Bullets" className="toolbar-btn" disabled={readOnly}>
                        •
                    </button>
                    <button type="button" onClick={() => formatText('insertOrderedList')} title="Numbered List" className="toolbar-btn" disabled={readOnly}>
                        1.
                    </button>
                    <button type="button" onClick={() => formatText('formatBlock', 'blockquote')} title="Quote" className="toolbar-btn" disabled={readOnly}>
                        "
                    </button>
                    <button type="button" onClick={() => formatText('removeFormat')} title="Clear Formatting" className="toolbar-btn" disabled={readOnly}>
                        ✗
                    </button>
                    <select
                        className="toolbar-select"
                        value={fontFamily}
                        title="Font"
                        disabled={readOnly}
                        onMouseDown={saveSelection}
                        onChange={(e) => applyFontName(e.target.value)}
                    >
                        {(FONT_OPTIONS.includes(fontFamily) ? FONT_OPTIONS : [fontFamily, ...FONT_OPTIONS]).map((f) => (
                            <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                        ))}
                    </select>
                    <select
                        className="toolbar-select toolbar-select-size"
                        value={fontSize}
                        title="Font Size"
                        disabled={readOnly}
                        onMouseDown={saveSelection}
                        onChange={(e) => applyFontSize(e.target.value)}
                    >
                        <option value="" disabled>Size</option>
                        {FONT_SIZES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <label className="toolbar-color-label" title="Font Colour" onMouseDown={saveSelection}>
                        <span className="toolbar-color-icon" style={{ borderBottomColor: '#c00000' }}>A</span>
                        <input
                            type="color"
                            className="toolbar-color-input"
                            defaultValue="#000000"
                            disabled={readOnly}
                            onChange={(e) => applyColor('foreColor', e.target.value)}
                        />
                    </label>
                    <label className="toolbar-color-label" title="Text Highlight Colour" onMouseDown={saveSelection}>
                        <span className="toolbar-color-icon" style={{ borderBottomColor: '#ffc000', background: '#fff3b0' }}>ab</span>
                        <input
                            type="color"
                            className="toolbar-color-input"
                            defaultValue="#ffff00"
                            disabled={readOnly}
                            onChange={(e) => applyColor('hiliteColor', e.target.value)}
                        />
                    </label>
                </div>
                <div
                    ref={detailsRef}
                    contentEditable={editable}
                    className="email-template-body"
                    onInput={handleDetailsInput}
                    suppressContentEditableWarning={true}
                    style={{ direction: 'ltr', unicodeBidi: 'embed', marginTop: 0 }}
                />
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ fontSize: 13, fontWeight: 'bold' }}>Link to Job</label>
                {resolvedJob ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 12 }}>
                        <a
                            href="#"
                            onClick={e => { e.preventDefault(); openJobPreview(); }}
                            style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                            title="View job (read only)"
                        >
                            {jobLoading ? 'Loading…' : <strong>{resolvedJob.label}</strong>}
                        </a>
                        {editable && (
                            <button
                                type="button"
                                className="icon-btn cancel-btn"
                                title="Remove link"
                                onClick={() => { setResolvedJob(null); setJobTerm(''); }}
                            >
                                <i className="fa fa-times" />
                            </button>
                        )}
                    </div>
                ) : editable ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                        <div style={{ width: 110, flexShrink: 0 }}>
                            <select
                                className="search-input"
                                style={{ width: '100%', minWidth: 0, fontSize: 12, padding: '4px 6px', height: 30 }}
                                value={jobField}
                                onChange={e => { setJobField(e.target.value); setJobTerm(''); setJobOptions([]); setJobDropdownOpen(false); }}
                            >
                                {JOB_SEARCH_FIELDS.map(f => (
                                    <option key={f.id} value={f.id}>{f.text}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                type="text"
                                className="search-input"
                                style={{ width: '100%', minWidth: 0, paddingRight: 28 }}
                                placeholder="Search..."
                                value={jobTerm}
                                onChange={e => { setJobTerm(e.target.value); setJobActiveIdx(-1); }}
                                onFocus={() => { if (jobOptions.length > 0) setJobDropdownOpen(true); }}
                                onBlur={() => setTimeout(() => setJobDropdownOpen(false), 200)}
                                onKeyDown={e => {
                                    if (e.key === 'ArrowDown') { e.preventDefault(); setJobActiveIdx(i => Math.min(i + 1, jobOptions.length - 1)); }
                                    else if (e.key === 'ArrowUp') { e.preventDefault(); setJobActiveIdx(i => Math.max(i - 1, 0)); }
                                    else if (e.key === 'Enter' && jobActiveIdx >= 0) { e.preventDefault(); selectJobOption(jobOptions[jobActiveIdx]); }
                                    else if (e.key === 'Escape') { setJobDropdownOpen(false); }
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => fetchJobOptions(jobTerm, jobField)}
                                style={{
                                    position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
                                    background: 'transparent', border: 'none', cursor: 'pointer', color: '#555', fontSize: 13, padding: '0 4px'
                                }}
                                title="Search"
                            >
                                <i className="fa fa-search" />
                            </button>
                            {jobDropdownOpen && (
                                <div style={{
                                    position: 'absolute', top: '110%', left: 0,
                                    width: '100%', minWidth: 320, background: '#fff', border: '1px solid #ccc',
                                    borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                                    zIndex: 9999, maxHeight: 240, overflowY: 'auto'
                                }}>
                                    {jobSearching && <div style={{ padding: '6px 10px', color: '#888', fontSize: 12 }}>Searching...</div>}
                                    {!jobSearching && jobOptions.length === 0 && (
                                        <div style={{ padding: '6px 10px', color: '#888', fontSize: 12 }}>No matching jobs.</div>
                                    )}
                                    {jobOptions.map((opt, idx) => (
                                        <div
                                            key={opt.id}
                                            onClick={() => selectJobOption(opt)}
                                            onMouseEnter={() => setJobActiveIdx(idx)}
                                            style={{
                                                padding: '7px 12px', cursor: 'pointer', fontSize: 12,
                                                background: idx === jobActiveIdx ? '#0d6efd' : '#fff',
                                                color: idx === jobActiveIdx ? '#fff' : '#212529',
                                                borderBottom: '1px solid #f0f0f0'
                                            }}
                                        >
                                            {getJobDisplay(opt)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>No job linked</div>
                )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ fontSize: 13, fontWeight: 'bold' }}>Attachments</label>
                {existingAttachments.length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {existingAttachments.map(att => (
                            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                                <a
                                    href="#"
                                    onClick={e => { e.preventDefault(); openFilePreview(att.file_url, att.name); }}
                                    style={{ color: '#007bff' }}
                                >
                                    {att.name}
                                </a>
                                {editable && (
                                    <button
                                        type="button"
                                        className="icon-btn cancel-btn"
                                        title="Remove"
                                        onClick={() => {
                                            setExistingAttachments(prev => prev.filter(a => a.id !== att.id));
                                            setRemoveIds(prev => [...prev, att.id]);
                                        }}
                                    >
                                        <i className="fa fa-times" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {editable && (
                    <input
                        type="file"
                        multiple
                        style={{ marginTop: 6 }}
                        onChange={e => {
                            const picked = Array.from(e.target.files || []);
                            if (picked.length) setFiles(prev => [...prev, ...picked]);
                            e.target.value = ''; // allow picking the same file again
                        }}
                    />
                )}
                {files.length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {files.map((f2, idx) => (
                            <div key={`${f2.name}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                                <span style={{ color: '#555' }}>{f2.name}</span>
                                <button
                                    type="button"
                                    className="icon-btn cancel-btn"
                                    title="Remove"
                                    onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                                >
                                    <i className="fa fa-times" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {canDelete && (
                <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{ fontSize: 13, fontWeight: 'bold' }}>Departments allowed to view</label>
                    <MultiSelectDropdown
                        id="qr-departments"
                        options={departments}
                        selectedValues={deptIds}
                        onChange={vals => setDeptIds((vals || []).map(String))}
                        placeholder="Select departments"
                        displayKey="text"
                        valueKey="id"
                        disabled={readOnly}
                    />
                </div>
            )}
            </div>

            {rejectPrompt && (
                <div style={{ border: '1px solid #c62828', borderRadius: 4, padding: 10, background: '#fff5f5' }}>
                    <label style={{ fontSize: 12, fontWeight: 'bold', color: '#c62828' }}>Rejection Reason</label>
                    <textarea
                        rows={2}
                        style={{ width: '100%', marginTop: 4, padding: 6, border: '1px solid #ccc', borderRadius: 4, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                        placeholder="Enter reason for rejection..."
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        autoFocus
                    />
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
                        <button
                            type="button"
                            className="dialog-btn"
                            style={{ background: '#c62828', color: '#fff' }}
                            disabled={!!deciding}
                            onClick={() => { setRejectPrompt(false); handleDecision('reject', rejectReason.trim()); }}
                        >
                            {deciding === 'reject' ? 'Rejecting…' : 'Confirm Reject'}
                        </button>
                        <button type="button" className="dialog-btn dialog-btn-secondary" onClick={() => setRejectPrompt(false)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', paddingTop: 12 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                    {canDelete && mode !== 'add' && status !== 'APPROVED' && (
                        <button
                            type="button"
                            className="dialog-btn"
                            style={{ background: '#2e7d32', color: '#fff' }}
                            disabled={!!deciding}
                            onClick={() => handleDecision('approve')}
                        >
                            {deciding === 'approve' ? 'Approving…' : 'Approve'}
                        </button>
                    )}
                    {canDelete && mode !== 'add' && status !== 'REJECTED' && (
                        <button
                            type="button"
                            className="dialog-btn"
                            style={{ background: '#c62828', color: '#fff' }}
                            disabled={!!deciding}
                            onClick={() => { setRejectReason(''); setRejectPrompt(true); }}
                        >
                            {deciding === 'reject' ? 'Rejecting…' : 'Reject'}
                        </button>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="dialog-btn dialog-btn-secondary" onClick={onClose}>
                        {readOnly ? 'Close' : 'Cancel'}
                    </button>
                    {editable && (
                        <button type="button" className="dialog-btn dialog-btn-primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    )}
                </div>
            </div>

            {jobPreview.open && jobPreview.job && (
                <PopoutWindow
                    title={`Job ${jobPreview.job.id} — View Only`}
                    name={`qr_form_job_view_${jobPreview.job.id}`}
                    onClose={() => setJobPreview({ open: false, job: null })}
                >
                    <JobForm
                        mode="view"
                        initialData={jobPreview.job}
                        onClose={() => setJobPreview({ open: false, job: null })}
                        documentsEditable={false}
                    />
                </PopoutWindow>
            )}
        </div>
    );
};

export default QuickReferenceForm;
